import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Tab,
  Tabs,
  Badge,
  Avatar,
} from '@mui/material';
import {
  VideoCall,
  Schedule,
  Person,
  Assignment,
  Notifications,
  Assessment,
  Add,
} from '@mui/icons-material';
import { appointmentAPI, Appointment } from '../services/apiService';
import VideoConsultation from '../components/VideoConsultation';
import AssessmentReviewTab from '../components/provider/AssessmentReviewTab';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`provider-tabpanel-${index}`}
      aria-labelledby={`provider-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ProviderDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [videoConsultation, setVideoConsultation] = useState<{
    open: boolean;
    appointment: Appointment | null;
  }>({
    open: false,
    appointment: null,
  });
  const [selectedAppointmentForReview, setSelectedAppointmentForReview] = useState<Appointment | null>(null);

  useEffect(() => {
    loadAppointments();
  }, []);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await appointmentAPI.getAppointments();
      // Sort by date (upcoming first)
      const sortedAppointments = data.sort((a, b) => 
        new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime()
      );
      setAppointments(sortedAppointments);
    } catch (err) {
      setError('Failed to load appointments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const openVideoConsultation = (appointment: Appointment) => {
    setVideoConsultation({
      open: true,
      appointment,
    });
  };

  const closeVideoConsultation = () => {
    setVideoConsultation({
      open: false,
      appointment: null,
    });
  };

  const canStartMeeting = (appointment: Appointment) => {
    if (appointment.status !== 'booked') return false;
    
    const appointmentTime = new Date(appointment.dateTime);
    const now = new Date();
    const timeDiffMinutes = (appointmentTime.getTime() - now.getTime()) / (1000 * 60);
    
    // Allow starting 30 minutes before appointment time and up to 3 hours after
    return timeDiffMinutes <= 30 && timeDiffMinutes >= -180;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'booked': return 'success';
      case 'completed': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const formatAppointmentDateTime = (dateTime: string) => {
    const date = new Date(dateTime);
    return {
      date: date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      }),
      time: date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    };
  };

  const isToday = (dateTime: string) => {
    const appointmentDate = new Date(dateTime);
    const today = new Date();
    return appointmentDate.toDateString() === today.toDateString();
  };

  const upcomingAppointments = appointments.filter(apt => 
    new Date(apt.dateTime) > new Date() && apt.status === 'booked'
  );

  const todaysAppointments = upcomingAppointments.filter(apt => isToday(apt.dateTime));
  const pastAppointments = appointments.filter(apt => 
    new Date(apt.dateTime) <= new Date() || apt.status !== 'booked'
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <div>
          <Typography variant="h3" component="h1" gutterBottom>
            Provider Dashboard
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Manage your FMLA consultations and patient appointments
          </Typography>
        </div>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/provider/availability')}
            sx={{ minWidth: 160 }}
          >
            Set Availability
          </Button>
          <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
            <Person />
          </Avatar>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Badge badgeContent={todaysAppointments.length} color="primary">
                <Schedule sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              </Badge>
              <Typography variant="h6" gutterBottom>
                Today's Appointments
              </Typography>
              <Typography variant="h4" color="primary">
                {todaysAppointments.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <VideoCall sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Active Meetings
              </Typography>
              <Typography variant="h4" color="success.main">
                {appointments.filter(apt => apt.consultationStatus === 'active').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Assignment sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Total Patients
              </Typography>
              <Typography variant="h4" color="info.main">
                {new Set(appointments.map(apt => apt.patientEmail)).size}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Notifications sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Pending Actions
              </Typography>
              <Typography variant="h4" color="warning.main">
                {appointments.filter(apt => canStartMeeting(apt)).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Navigation Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="provider dashboard tabs">
          <Tab label={`Today's Schedule (${todaysAppointments.length})`} />
          <Tab label={`Upcoming (${upcomingAppointments.length - todaysAppointments.length})`} />
          <Tab label={`Past Appointments (${pastAppointments.length})`} />
          <Tab label="Assessment Reviews" />
        </Tabs>
      </Box>

      {/* Today's Appointments */}
      <TabPanel value={tabValue} index={0}>
        {todaysAppointments.length === 0 ? (
          <Card sx={{ textAlign: 'center', py: 6 }}>
            <CardContent>
              <Schedule sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                No appointments today
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Your schedule is clear for today
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {todaysAppointments.map((appointment) => {
              const { date, time } = formatAppointmentDateTime(appointment.dateTime);
              return (
                <Grid item xs={12} key={appointment.appointmentId}>
                  <Card sx={{ 
                    borderLeft: canStartMeeting(appointment) ? '4px solid' : 'none',
                    borderLeftColor: 'success.main'
                  }}>
                    <CardContent>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={6}>
                          <Typography variant="h6" gutterBottom>
                            {time}
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            Patient: {appointment.patientEmail}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Appointment ID: {appointment.appointmentId.slice(0, 8)}...
                          </Typography>
                          {appointment.consultationStatus && (
                            <Chip 
                              label={appointment.consultationStatus.toUpperCase()}
                              size="small"
                              color={appointment.consultationStatus === 'active' ? 'success' : 'default'}
                              sx={{ mt: 1 }}
                            />
                          )}
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Chip 
                            label={appointment.status.toUpperCase()} 
                            color={getStatusColor(appointment.status) as any}
                            sx={{ mb: 1 }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3} textAlign="right">
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Button 
                              variant={canStartMeeting(appointment) ? "contained" : "outlined"} 
                              startIcon={<VideoCall />}
                              size="small"
                              disabled={!canStartMeeting(appointment)}
                              onClick={() => openVideoConsultation(appointment)}
                              sx={{ 
                                backgroundColor: canStartMeeting(appointment) ? 'success.main' : undefined,
                                '&:hover': {
                                  backgroundColor: canStartMeeting(appointment) ? 'success.dark' : undefined,
                                },
                              }}
                            >
                              {canStartMeeting(appointment) ? 'Start Meeting' : 'Meeting Not Ready'}
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </TabPanel>

      {/* Upcoming Appointments */}
      <TabPanel value={tabValue} index={1}>
        {upcomingAppointments.filter(apt => !isToday(apt.dateTime)).length === 0 ? (
          <Card sx={{ textAlign: 'center', py: 6 }}>
            <CardContent>
              <Schedule sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                No upcoming appointments
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Your upcoming schedule is clear
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {upcomingAppointments
              .filter(apt => !isToday(apt.dateTime))
              .map((appointment) => {
                const { date, time } = formatAppointmentDateTime(appointment.dateTime);
                return (
                  <Grid item xs={12} key={appointment.appointmentId}>
                    <Card>
                      <CardContent>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={8}>
                            <Typography variant="h6" gutterBottom>
                              {date} at {time}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              Patient: {appointment.patientEmail}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Appointment ID: {appointment.appointmentId.slice(0, 8)}...
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={4} textAlign="right">
                            <Chip 
                              label={appointment.status.toUpperCase()} 
                              color={getStatusColor(appointment.status) as any}
                            />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
          </Grid>
        )}
      </TabPanel>

      {/* Past Appointments */}
      <TabPanel value={tabValue} index={2}>
        {pastAppointments.length === 0 ? (
          <Card sx={{ textAlign: 'center', py: 6 }}>
            <CardContent>
              <Assignment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                No past appointments
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Your appointment history will appear here
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {pastAppointments.map((appointment) => {
              const { date, time } = formatAppointmentDateTime(appointment.dateTime);
              return (
                <Grid item xs={12} key={appointment.appointmentId}>
                  <Card sx={{ opacity: 0.8 }}>
                    <CardContent>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={8}>
                          <Typography variant="h6" gutterBottom>
                            {date} at {time}
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            Patient: {appointment.patientEmail}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Appointment ID: {appointment.appointmentId.slice(0, 8)}...
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={4} textAlign="right">
                          <Chip 
                            label={appointment.status.toUpperCase()} 
                            color={getStatusColor(appointment.status) as any}
                          />
                          {appointment.cancelledBy && (
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                              Cancelled by: {appointment.cancelledBy}
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </TabPanel>

      {/* Assessment Reviews */}
      <TabPanel value={tabValue} index={3}>
        {selectedAppointmentForReview ? (
          <Box>
            <Button 
              onClick={() => setSelectedAppointmentForReview(null)}
              sx={{ mb: 2 }}
            >
              ← Back to Appointment List
            </Button>
            <AssessmentReviewTab 
              appointmentId={selectedAppointmentForReview.appointmentId}
              patientEmail={selectedAppointmentForReview.patientEmail!}
            />
          </Box>
        ) : (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select an Appointment to Review Assessments
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Choose an appointment to review patient assessments and generate FMLA forms.
            </Typography>
            <Grid container spacing={3}>
              {appointments.filter(apt => apt.status === 'completed' || apt.status === 'booked').map((appointment) => {
                const { date, time } = formatAppointmentDateTime(appointment.dateTime);
                return (
                  <Grid item xs={12} md={6} key={appointment.appointmentId}>
                    <Card sx={{ cursor: 'pointer' }} onClick={() => setSelectedAppointmentForReview(appointment)}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {date} at {time}
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          Patient: {appointment.patientEmail}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Appointment ID: {appointment.appointmentId.slice(0, 8)}...
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                          <Chip 
                            label={appointment.status.toUpperCase()} 
                            color={getStatusColor(appointment.status) as any}
                            size="small"
                          />
                          <Chip 
                            label="Review Assessments" 
                            color="primary"
                            size="small"
                            icon={<Assessment />}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
              {appointments.filter(apt => apt.status === 'completed' || apt.status === 'booked').length === 0 && (
                <Grid item xs={12}>
                  <Card sx={{ textAlign: 'center', py: 6 }}>
                    <CardContent>
                      <Assessment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h5" gutterBottom>
                        No Appointments Available for Review
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Completed or scheduled appointments will appear here for assessment review.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </TabPanel>

      {/* Video Consultation Dialog */}
      {videoConsultation.appointment && (
        <VideoConsultation
          appointment={videoConsultation.appointment}
          open={videoConsultation.open}
          onClose={closeVideoConsultation}
          userType="provider"
        />
      )}
    </Container>
  );
};

export default ProviderDashboard;